import { useEffect, useRef, useState } from "react";
import { Cancel01Icon } from "@hugeicons-pro/core-solid-standard";
import { HugeiconsIcon } from "@hugeicons/react";
import { UPLOAD_FILE_ACCEPT } from "utils/files/uploadFileTypes";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import LocalFileImagePreview from "./LocalFileImagePreview";
import { useSupportedReceiptFiles } from "hooks/useSupportedReceiptFiles";
import { toFileList } from "utils/receipts/receiptUpload";
import { CloudUploadIcon } from "@hugeicons-pro/core-stroke-rounded";
import { IconButton } from "@outpave/ds";

const SAFE_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/gif",
]);

interface UploadReceiptReviewProps {
  files: File[];
  onRemoveFile: (index: number) => void;
  onAddFiles: (files: FileList) => void;
}

const UploadReceiptReview: React.FC<UploadReceiptReviewProps> = ({
  files,
  onRemoveFile,
  onAddFiles,
}) => {
  const { t } = useTranslation();
  const keepSupported = useSupportedReceiptFiles();
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileInputKey, setFileInputKey] = useState(0);

  // Some browsers won't re-fire `onChange` for the same file selection even after clearing
  // the input value. Remounting the input reliably resets that internal state.
  useEffect(() => {
    setFileInputKey((k) => k + 1);
  }, [files]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-5)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-5)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-2)",
          }}
        >
          <Typography variant="h5" component="span">
            {t("Review before you go")}
          </Typography>
          <Typography variant="body2" component="span">
            {t(
              "Make sure everything looks right You won't be able to cancel once scanning starts",
            )}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-3)",
          }}
        >
          <Typography variant="label" component="span">
            {t("Uploaded Receipts")}
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "var(--space-4)",
            }}
          >
            {files.map((file, index) => {
              const fileKey = `${file.name}-${file.size}-${file.lastModified}-${index}`;
              return (
                <Box
                  key={fileKey}
                  sx={{
                    position: "relative",
                    borderRadius: "var(--radius-lg)",
                    overflow: "hidden",
                    aspectRatio: "1/1",
                    border: "var(--border-width-1) solid var(--color-border)",
                  }}
                >
                  {SAFE_IMAGE_TYPES.has(file.type) ? (
                    <LocalFileImagePreview file={file} />
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        aspectRatio: "1/1",
                        backgroundColor: "var(--color-bg-surface)",
                        borderRadius: "var(--radius-lg)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "var(--space-2)",
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{
                          color: "var(--color-text-secondary)",
                          wordBreak: "break-all",
                        }}
                      >
                        {file.name}
                      </Typography>
                    </Box>
                  )}
                  <IconButton
                    aria-label={t("Remove receipt")}
                    size="small"
                    onClick={() => onRemoveFile(index)}
                    sx={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                    }}
                  >
                    <HugeiconsIcon icon={Cancel01Icon} size={16} />
                  </IconButton>
                </Box>
              );
            })}

            {/* Add Receipt tile */}
            <Box
              component="button"
              type="button"
              onClick={() => {
                // Allow re-selecting the same file after removing it.
                if (inputRef.current) inputRef.current.value = "";
                inputRef.current?.click();
              }}
              sx={{
                width: "100%",
                aspectRatio: "1/1",
                backgroundColor: "var(--color-bg-elevated)",
                borderRadius: "var(--radius-lg)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "var(--space-1)",
                border: "none",
                cursor: "pointer",
                padding: "var(--space-1)",
                "&:focus-visible": {
                  outline:
                    "var(--border-width-2) solid var(--color-border-focus)",
                  outlineOffset: "var(--border-width-1)",
                },
              }}
            >
              <HugeiconsIcon
                icon={CloudUploadIcon}
                size={20}
                color="var(--color-text-secondary)"
              />
              <Typography variant="body1" component="span">
                {t("Add Receipt")}
              </Typography>
              <Typography
                variant="caption"
                component="span"
                sx={{ color: "var(--color-text-tertiary)" }}
              >
                {t("JPG, PNG,\nWEBP, PDF\n(Max 10 MB)")}
              </Typography>
            </Box>

            <input
              key={fileInputKey}
              ref={inputRef}
              type="file"
              accept={UPLOAD_FILE_ACCEPT}
              hidden
              multiple
              onChange={(e) => {
                const supported = keepSupported(e.target.files ?? []);
                if (supported.length > 0) onAddFiles(toFileList(supported));
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default UploadReceiptReview;
